import {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {Analytics} from '../../../services/analytics';
import * as ExpectedPanelsActions from '../../../state/expectedPanels/actions';
import {mergeViz, patchVizMap} from '../../../state/graphql/historyKeysQuery';
import {useReduxAction, useReduxStore} from '../../../state/hooks';
import {useActiveHistoryKeyInfo} from '../../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import {usePanelGenHistoryKeysStoreSync} from '../../../state/panelGenHistoryKeys/usePanelGenHistoryKeysStoreSync';
import {RunQueryContext} from '../../../state/runs/context';
import {type useRunsWandbConfigQuery} from '../../../state/runs/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import {denormalize} from '../../../state/views/normalize';
import * as PanelBankConfigActionsInternal from '../../../state/views/panelBankConfig/actionsInternal';
import type * as PanelBankConfigTypes from '../../../state/views/panelBankConfig/types';
import {type WholeFromTypeWithRef} from '../../../state/views/types';
import {getCache, getCacheStorageKey} from '../../../util/cache';
import {recordRumDurationVital} from '../../../util/datadog/datadogRum';
import {isFetchFailureError} from '../../../util/errors';
import {getExpectedPanelSpecs} from '../../../util/panelbank';
import {useProjectRsaReadEnabledWithLoading} from '../../../util/rampFeatureFlags/projectRsaReadEnabled.query';
import {
  useRampFlagSparseCompression,
  useRampFlagSparseOverrides,
  useStatsigGateEvalTables,
  useStatsigGateEvalTablesCes,
} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useCheckWeaveSignal} from '../../../util/useWeaveTrace';
import {getEvaluationTablePanelKeys} from '../../PanelEvaluationTables/discovery';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {
  useAutoOrganizePrefix,
  useShouldAutoGeneratePanels,
} from '../../WorkspaceDrawer/Settings/hooks';
import {useDefinedMetricContext} from '../contextProviders/DefinedMetricContext';

export const useAutoSyncPanelBank = (
  panelBankConfigRef: PanelBankConfigTypes.Ref,
  wbConfigQuery: ReturnType<typeof useRunsWandbConfigQuery>
): {loading: boolean} => {
  // Initialize the sparse overrides feature flags early.
  // This caches the Statsig flag values for synchronous access in reducers and compression.
  useRampFlagSparseOverrides();
  useRampFlagSparseCompression();

  const updateExpectedPanels = useReduxAction(ExpectedPanelsActions.update);
  const clearExpectedPanels = useReduxAction(ExpectedPanelsActions.clear);
  const diffAndInitPanels = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActionsInternal.diffAndInitPanels
  );

  // Fetch/poll section-aware history keys and sync results to store
  usePanelGenHistoryKeysStoreSync();

  const {
    historyKeyInfo,
    loading: activeHistoryKeysLoading,
    error: historyKeysError,
  } = useActiveHistoryKeyInfo();

  // Wait for the accelerator-promotion answer before syncing, so we don't
  // briefly generate panels from the legacy history-keys query on a project
  // that turns out to be running NGW.
  const routeProject = useProjectParams();
  const {loading: isNgwLoading} = useProjectRsaReadEnabledWithLoading(
    routeProject.entityName,
    routeProject.projectName
  );
  const historyKeysLoading = isNgwLoading || activeHistoryKeysLoading;

  // a failed fetch is a network blip and the polled query refetches on its
  // own, so keep the workspace up instead of crashing it
  const isFatalHistoryKeysError =
    historyKeysError != null && !isFetchFailureError(historyKeysError);
  if (isFatalHistoryKeysError) {
    throw historyKeysError;
  }

  const autoOrganizePrefix = useAutoOrganizePrefix();
  const shouldAutoGeneratePanels = useShouldAutoGeneratePanels();
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const {definedMetricsMap, hiddenMetrics} = useDefinedMetricContext();
  const replacePanelBankConfig = useReduxAction(
    PanelBankConfigActionsInternal.replacePanelBankConfig
  );

  const [loading, setLoading] = useState<boolean>(true);

  const cache = getCache();

  const latestCacheKey = useRef<string | null>(null);

  const store = useReduxStore();

  const isFirstPBRender = useRef(true);

  // Heuristic: include Weave Trace panel only if project has trace data
  const shouldIncludeWeavePanel = useCheckWeaveSignal();
  const isEvalTablesEnabled = useStatsigGateEvalTables() ?? false;
  const isEvalTablesCesEnabled = useStatsigGateEvalTablesCes();
  const evaluationTablePanelKeys = useMemo(
    () =>
      getEvaluationTablePanelKeys(
        historyKeyInfo.keys,
        isEvalTablesEnabled,
        isEvalTablesCesEnabled
      ),
    [historyKeyInfo.keys, isEvalTablesCesEnabled, isEvalTablesEnabled]
  );

  const {entityName, projectName} = useContext(RunQueryContext);
  useEffect(
    function autoSyncPanelBankEffect() {
      if (
        historyKeysLoading ||
        historyKeysError ||
        wbConfigQuery.loading ||
        wbConfigQuery.error ||
        !wbConfigQuery.wandbConfig
      ) {
        return;
      }

      // One duration vital spans the whole effect flow, including the async
      // cache branch, so it is emitted at each terminal point of the flow
      // rather than at the end of this (synchronous) function body. Substep
      // timings are reported as `<name>Span` context fields on the same vital
      // so every span comes from the same execution; `<name>Count` reports how
      // many calls each span sums over. Monotonic marks for
      // durations, epoch-anchored start for the vital (see the duration-vital
      // guidance in util/datadog/datadogRum.ts).
      const effectStartMark = performance.now();
      const spans: Record<string, number> = {};
      const measure = <T>(name: string, fn: () => T): T => {
        const start = performance.now();
        const result = fn();
        spans[`${name}Span`] =
          (spans[`${name}Span`] ?? 0) + performance.now() - start;
        spans[`${name}Count`] = (spans[`${name}Count`] ?? 0) + 1;
        return result;
      };

      const viz = patchVizMap(mergeViz(wbConfigQuery.wandbConfig ?? []));

      // Captured before this effect flips the ref at the bottom of the body:
      // the async cache branch emits the vital after that flip has happened.
      const wasFirstRender = isFirstPBRender.current;
      let expectedPanelCount: number | undefined;

      const emitAutoSyncVital = (
        // cache-hit / cache-miss / no-cache-file: terminal points of the
        // first-render async cache branch. resync: every later effect run,
        // which skips the cache round-trip entirely (getCache() never returns
        // null, so "cache unavailable" is not a real path).
        path: 'cache-hit' | 'cache-miss' | 'no-cache-file' | 'resync'
      ) => {
        const duration = performance.now() - effectStartMark;
        const context = {
          path,
          keyCount: historyKeyInfo.sortedKeys.length,
          expectedPanelCount,
          isFirstRender: wasFirstRender,
          ...spans,
        };

        recordRumDurationVital('workspace.autosync_panel_bank', {
          startTime: performance.timeOrigin + effectStartMark,
          duration,
          description:
            'panel-bank auto-sync effect: cache check + panel diff/init',
          context,
        });
      };

      // We need this to not have any refs in the cache key
      // so that our cache key can be stable. Thats why we
      // use denormalize here. We also want to be very conservative
      // about calling denormalize because it's expensive. So we only
      // want to run it if the history keys or config have changed.
      const getCurrentPanelBankConfig = () =>
        measure('denormalizeConfig', function denormalizePanelBankConfig() {
          return denormalize(store.getState().views.parts, panelBankConfigRef, {
            includeRefs: false,
          });
        });

      // Also covers the updateExpectedPanels dispatch: its reducer stores a
      // reference (O(1)), so a separate span for it would be immaterial.
      const populateExpectedPanels = () =>
        measure('expectedPanelSpecs', function populateExpectedPanelSpecs() {
          const expectedPanels = getExpectedPanelSpecs(
            historyKeyInfo.keys,
            viz,
            autoOrganizePrefix,
            definedMetricsMap,
            hiddenMetrics,
            shouldIncludeWeavePanel,
            isEvalTablesEnabled,
            isEvalTablesCesEnabled
          );
          updateExpectedPanels(expectedPanels);
          expectedPanelCount =
            Object.keys(expectedPanels.defaultSpecs).length +
            Object.keys(expectedPanels.apiAddedSpecs).length;
          return expectedPanels;
        });

      const doDiffAndInitPanels = () => {
        const expectedPanels = populateExpectedPanels();
        if (shouldIncludeWeavePanel) {
          Analytics.track('Weave Trace Panel Is Included', {
            entityName,
            projectName,
          });
        }
        if (evaluationTablePanelKeys.length > 0) {
          Analytics.track('Evaluation Tables Panels Are Included', {
            entityName,
            projectName,
            count: evaluationTablePanelKeys.length,
          });
        }
        measure('diffAndInit', function dispatchDiffAndInitPanels() {
          diffAndInitPanels(expectedPanels, workspaceSettingsRef);
        });
        setLoading(false);
      };

      if (cache && isFirstPBRender.current) {
        // Built here rather than in the effect body because this branch is its
        // only reader. Every later run of the effect (the resync path) skips
        // the cache round-trip entirely, and constructing the key eagerly made
        // those runs pay a full denormalize whose result was discarded.

        // must include `sortAlphabetically` workspace setting in cache key,
        // but we don't want to trigger panel gen whenever alpha sort setting
        // is touched. therefore, we have to get the setting value directly
        // from the store *inside* this effect hook
        const {sortAlphabetically} =
          store.getState().views.parts[workspaceSettingsRef.type][
            workspaceSettingsRef.id
          ] ?? {};

        const cacheKey = {
          keys: historyKeyInfo.sortedKeys,
          viz,
          panelBankConfig: getCurrentPanelBankConfig(),
          autoOrganizePrefix,
          shouldAutoGeneratePanels,
          sortAlphabetically,
          definedMetricsMap,
          hiddenMetrics,
          shouldIncludeWeavePanel,
          isEvalTablesEnabled,
          isEvalTablesCesEnabled,
          evaluationTablePanelKeys,
        };

        // Sizes the main-thread cost of hashing the panel-bank cache key
        // (JSON.stringify + fnv over a key that embeds every history key name).
        const hashStartMark = performance.now();
        const fileName = getCacheStorageKey('autoSyncPanelBank', cacheKey);
        const hashDuration = performance.now() - hashStartMark;
        spans.cacheKeySpan = hashDuration;
        recordRumDurationVital('workspace.autosync_cache_key', {
          startTime: performance.timeOrigin + hashStartMark,
          duration: hashDuration,
          description: 'panel-bank auto-sync cache storage key computation',
          context: {keyCount: historyKeyInfo.sortedKeys.length},
        });

        latestCacheKey.current = fileName;

        (async function readPanelBankCacheAndSync() {
          if (fileName) {
            const cacheGetStartMark = performance.now();
            const cachedData = await cache.get<
              WholeFromTypeWithRef<'panel-bank-config'>
            >(fileName, 'autoSyncPanelBank');
            spans.cacheGetSpan = performance.now() - cacheGetStartMark;

            // TODO: dead guard — latestCacheKey is assigned exactly once per
            // mount (this async branch only runs on first render, gated by
            // isFirstPBRender), so this comparison never fails. Remove or
            // rewire in a follow-up cleanup.
            if (fileName !== latestCacheKey.current) {
              // stale request, ignore (no vital: abandoned partial flows would
              // pollute the duration distribution)
              return;
            }

            if (cachedData) {
              measure(
                'replaceConfig',
                function dispatchReplacePanelBankConfig() {
                  replacePanelBankConfig(cachedData.data, panelBankConfigRef);
                }
              );
              setLoading(false);
              // still need to populate expected panels state after pb cache hit,
              // otherwise there won't be any expected panels for panel quick add
              populateExpectedPanels();
              emitAutoSyncVital('cache-hit');
            } else {
              doDiffAndInitPanels();
              // NOTE: second denormalize of this run — diffAndInitPanels just
              // rewrote the config, so the payload must be re-captured (the
              // cacheKey snapshot above is the pre-generation state). measure()
              // sums repeated calls, so denormalizeConfigSpan reads ~2x on the
              // cache-miss path relative to every other path
              // (denormalizeConfigCount disambiguates in dashboards).
              const panelBankConfig = getCurrentPanelBankConfig();
              // Fire-and-forget: serialization happens in a later microtask
              // (set() awaits before stringifying) and is self-instrumented by
              // the cache layer (cache.serialize / cache.put perf timers), so
              // it is deliberately outside this vital.
              cache.set<WholeFromTypeWithRef<'panel-bank-config'>>(
                fileName,
                panelBankConfig,
                'autoSyncPanelBank'
              );
              emitAutoSyncVital('cache-miss');
            }
          } else {
            doDiffAndInitPanels();
            emitAutoSyncVital('no-cache-file');
          }
        })();
      } else {
        doDiffAndInitPanels();
        emitAutoSyncVital('resync');
      }

      isFirstPBRender.current = false;

      return () => {
        clearExpectedPanels();
      };
    },
    [
      historyKeyInfo,
      historyKeysLoading,
      historyKeysError,
      wbConfigQuery,
      definedMetricsMap,
      hiddenMetrics,
      autoOrganizePrefix,
      updateExpectedPanels,
      clearExpectedPanels,
      diffAndInitPanels,
      workspaceSettingsRef,
      setLoading,
      cache,
      panelBankConfigRef,
      replacePanelBankConfig,
      shouldAutoGeneratePanels,
      store,
      shouldIncludeWeavePanel,
      isEvalTablesEnabled,
      isEvalTablesCesEnabled,
      evaluationTablePanelKeys,
      entityName,
      projectName,
    ]
  );
  return useMemo(() => ({loading}), [loading]);
};
